/**
 * Suppliers (Phase 2 W5-2; docs/plans/PHASE_2_W5.md §3). Master data, edited online only. Names
 * never repeat (people pick a supplier from a searchable list, D251); a phone already used by
 * another supplier is only a warning (two branches of one company).
 */
import { z } from 'zod';

import { phoneSchema } from '../validation/issues.ts';

export const SUPPLIER_NAME_MAX = 120;
export const SUPPLIER_PHONES_MAX = 3;
export const SUPPLIER_TERMS_MAX_DAYS = 365;

const name = z.string().trim().min(2).max(SUPPLIER_NAME_MAX);
const optionalText = (max: number) => z.string().trim().max(max).nullable();

const fields = {
  nameAr: name,
  nameEn: name.nullable(),
  contactPerson: optionalText(120),
  phones: z.array(phoneSchema).max(SUPPLIER_PHONES_MAX),
  address: optionalText(300),
  category: optionalText(80),
  notes: optionalText(500),
  /** Days after the invoice date a credit invoice is due (0: at once). */
  paymentTermsDays: z.number().int().min(0).max(SUPPLIER_TERMS_MAX_DAYS),
};

export const SupplierFieldsSchema = z.strictObject({
  nameAr: fields.nameAr,
  nameEn: fields.nameEn.default(null),
  contactPerson: fields.contactPerson.default(null),
  phones: fields.phones.default([]),
  address: fields.address.default(null),
  category: fields.category.default(null),
  notes: fields.notes.default(null),
  paymentTermsDays: fields.paymentTermsDays.default(0),
});
export type SupplierFields = z.infer<typeof SupplierFieldsSchema>;
export type SupplierFieldsInput = z.input<typeof SupplierFieldsSchema>;

/** Only the fields sent change; every change carries a reason (audited). */
export const SupplierPatchSchema = z
  .strictObject({
    nameAr: fields.nameAr.optional(),
    nameEn: fields.nameEn.optional(),
    contactPerson: fields.contactPerson.optional(),
    phones: fields.phones.optional(),
    address: fields.address.optional(),
    category: fields.category.optional(),
    notes: fields.notes.optional(),
    paymentTermsDays: fields.paymentTermsDays.optional(),
    isActive: z.boolean().optional(),
    reason: z.string().trim().min(5).max(500),
  })
  .refine(
    (p) => Object.entries(p).some(([key, value]) => key !== 'reason' && value !== undefined),
    { message: 'Nothing to change' },
  );
export type SupplierPatch = z.infer<typeof SupplierPatchSchema>;
